/**
 * Firebase Web SDK (modular v9+) singletons + App Check + emulator wiring.
 *
 * App Check: a token is sent on every callable so enforcement can be flipped
 * on AFTER this build ships (spec §7: never enforce before the token-sending
 * client is live). In dev, set VITE_APPCHECK_DEBUG_TOKEN.
 */
import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);

const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';

// App Check: enable in deployed environments. In dev with emulators we use
// the debug token. The site key is a reCAPTCHA v3 key (see SETUP.md).
const reCaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
    import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
}
if (reCaptchaKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(reCaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(
  app,
  import.meta.env.VITE_FUNCTIONS_REGION ?? 'us-central1',
);

if (USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}
